import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UseSectionTrackingOptions {
  /** IDs of sections to track */
  sectionIds: string[];
  /** Distance from top where section is considered "active" */
  scrollMarginPx: number;
}
interface UseSectionTrackingReturn {
  /** Currently active section ID */
  activeSection: string;
  /** Scroll to a section by ID with smooth scrolling */
  scrollToSection: (id: string) => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Tracks which section is currently active based on scroll position.
 *
 * Features:
 * - Updates active section as user scrolls
 * - Handles programmatic scrolling with lock period to prevent jitter
 * - Uses RAF for performance
 * - Handles resize events
 */
export function useSectionTracking(t0) {
  const $ = _c(7);
  const {
    sectionIds,
    scrollMarginPx
  } = t0;
  const [activeSection, setActiveSection] = useState(sectionIds[0] ?? "");
  const programmaticTargetIdRef = useRef(null);
  const programmaticLockUntilRef = useRef(0);
  let t1;
  let t2;
  if ($[0] !== scrollMarginPx || $[1] !== sectionIds) {
    t1 = () => {
      if (sectionIds.length === 0) {
        return;
      }
      let rafId = null;
      const updateActiveSection = () => {
        const scrollMargin = scrollMarginPx;
        const targetId = programmaticTargetIdRef.current;
        if (targetId) {
          const now = typeof performance !== "undefined" ? performance.now() : Date.now();
          const targetEl = document.getElementById(targetId);
          if (!targetEl || now > programmaticLockUntilRef.current) {
            programmaticTargetIdRef.current = null;
          } else {
            const {
              top
            } = targetEl.getBoundingClientRect();
            const isLanded = Math.abs(top - scrollMargin) <= 2;
            if (!isLanded) {
              setActiveSection(prev => prev === targetId ? prev : targetId);
              return;
            }
            programmaticTargetIdRef.current = null;
          }
        }
        const sectionEls = sectionIds.map(_temp).filter(_temp2);
        let nextActive = sectionEls[0]?.id ?? sectionIds[0] ?? "";
        for (const el_0 of sectionEls) {
          const {
            top: top_0
          } = el_0.getBoundingClientRect();
          if (top_0 - scrollMargin <= 1) {
            nextActive = el_0.id;
          } else {
            break;
          }
        }
        setActiveSection(prev_0 => prev_0 === nextActive ? prev_0 : nextActive);
      };
      const scheduleUpdate = () => {
        if (rafId !== null) {
          return;
        }
        rafId = window.requestAnimationFrame(() => {
          rafId = null;
          updateActiveSection();
        });
      };
      window.addEventListener("scroll", scheduleUpdate, {
        passive: true
      });
      window.addEventListener("resize", scheduleUpdate, {
        passive: true
      });
      scheduleUpdate();
      return () => {
        window.removeEventListener("scroll", scheduleUpdate);
        window.removeEventListener("resize", scheduleUpdate);
        if (rafId !== null) {
          window.cancelAnimationFrame(rafId);
        }
      };
    };
    t2 = [sectionIds, scrollMarginPx];
    $[0] = scrollMarginPx;
    $[1] = sectionIds;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = id_0 => {
      const element = document.getElementById(id_0);
      if (!element) {
        return;
      }
      setActiveSection(id_0);
      programmaticTargetIdRef.current = id_0;
      const now_0 = typeof performance !== "undefined" ? performance.now() : Date.now();
      programmaticLockUntilRef.current = now_0 + 1500;
      element.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const scrollToSection = t3;
  let t4;
  if ($[5] !== activeSection) {
    t4 = {
      activeSection,
      scrollToSection
    };
    $[5] = activeSection;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function _temp2(el) {
  return el instanceof HTMLElement;
}
function _temp(id) {
  return document.getElementById(id);
}